import {Component, inject, input} from '@angular/core';
import {FormControl} from '@angular/forms';
import {take} from 'rxjs';

import {FieldConfig} from '../../models/form-config.model';
import {FIELD_INFO_RESOLVER} from '../../tokens/field-info.token';
import {ModalService} from '../../../../services/modal/modal-service';
import {ValidationMessagePipe} from '../../pipes/validation-message.pipe';
import {TextFieldComponent} from '../controls/text-field/text-field.component';
import {PasswordFieldComponent} from '../controls/password-field/password-field.component';
import {DropdownFieldComponent} from '../controls/dropdown-field/dropdown-field.component';
import {MultiselectFieldComponent} from '../controls/multiselect-field/multiselect-field.component';
import {RadioFieldComponent} from '../controls/radio-field/radio-field.component';
import {CheckboxFieldComponent} from '../controls/checkbox-field/checkbox-field.component';
import {TextareaFieldComponent} from '../controls/textarea-field/textarea-field.component';
import {AcceptanceFieldComponent} from '../controls/acceptance-field/acceptance-field.component';
import {ModuleToggleFieldComponent} from '../controls/module-toggle-field/module-toggle-field.component';
import {SecretStateFieldComponent} from '../controls/secret-state-field/secret-state-field.component';

/**
 * FieldRendererComponent — dispatcher dei tipi di campo.
 *
 * Riceve un FieldConfig e il FormControl corrispondente,
 * e renderizza il componente corretto in base a field.type.
 * Gestisce anche la visualizzazione dell'errore inline sotto il campo
 * tranne per il tipo 'acceptance' che si autogestisce nel suo componente.
 *
 * Questo componente è the confine tra l'engine e i singoli controlli:
 * aggiungere un nuovo tipo richiede solo di aggiungere un @case qui
 * e creare il relativo componente control.
 */
@Component({
  selector: 'app-field-renderer',
  standalone: true,
  imports: [
    ValidationMessagePipe,
    TextFieldComponent,
    PasswordFieldComponent,
    DropdownFieldComponent,
    MultiselectFieldComponent,
    RadioFieldComponent,
    CheckboxFieldComponent,
    TextareaFieldComponent,
    AcceptanceFieldComponent,
    ModuleToggleFieldComponent,
    SecretStateFieldComponent
  ],
  templateUrl: './field-renderer.component.html',
  styleUrl: './field-renderer.component.scss',
})
export class FieldRendererComponent {
  field = input.required<FieldConfig>();
  control = input.required<FormControl>();

  private readonly infoResolver = inject(FIELD_INFO_RESOLVER, {optional: true});
  private readonly modal = inject(ModalService);

  /** True se il campo ha un errore da mostrare (touched + invalid). */
  get showError(): boolean {
    // Per 'acceptance' l'errore è gestito internamente al componente
    if (this.field().type === 'acceptance') return false;
    return this.control()?.invalid && this.control()?.touched;
  }

  /** True se va mostrata l'icona informativa (infoId + provider disponibile). */
  get showInfo(): boolean {
    return this.field().infoId != null && !!this.infoResolver;
  }

  /** Apre il popup con il testo informativo del campo. */
  openInfo(): void {
    const infoId = this.field().infoId;
    if (infoId == null || !this.infoResolver) return;

    this.infoResolver(infoId).pipe(take(1)).subscribe({
      next: (testo) => {
        this.modal.openModal({
          title: this.field().label,
          description: testo,
          primaryButtonText: 'Chiudi',
        });
      },
    });
  }
}
